/**
 * Companion window store (CABINET) — COPILOT_MASTER_PLAN Phase 1 / subsystem A + I.
 *
 * Cabinet mirror of the client's useCompanionWindowStore. Holds the floating
 * AI-Companion window chrome state (open / minimized / dock mode / position /
 * size), the Assistant-vs-Agent mode preference (subsystem H) and the active
 * tab (chat | agent | tasks) + task-tray badge (subsystem E).
 *
 * Persisted to a CABINET-SPECIFIC localStorage key
 * ('sgrc-adminclient-companion-window') so the cabinet window state never
 * collides with the client portal running in the same browser.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/** float = free window · right = docked right panel (legacy Dialog behavior) ·
 *  bottom = docked bottom panel. On <640px viewports the renderer always uses
 *  a full-width bottom sheet regardless of this value (responsive fallback). */
export type CompanionDock = 'float' | 'right' | 'bottom'

/** Assistant = propose + confirm everything write (HITL) · Agent = plan + act. */
export type CompanionMode = 'assistant' | 'agent'

export type CompanionTab = 'chat' | 'agent' | 'tasks'

export type CompanionBadge = null | 'task-done' | 'hitl'

export const COMPANION_WINDOW_DEFAULTS = {
  w: 400,
  h: 600,
  minW: 320,
  minH: 420,
}

interface CompanionWindowState {
  open: boolean
  minimized: boolean
  dock: CompanionDock
  /** Top-left corner of the float window (px, viewport coordinates). */
  x: number
  y: number
  w: number
  h: number
  mode: CompanionMode
  activeTab: CompanionTab
  /** Badge shown on the minimized pill / tasks tab (completed task or pending HITL). */
  badge: CompanionBadge
  /** Number of unseen terminal task transitions since the tray was last viewed. */
  badgeCount: number

  openWindow: () => void
  closeWindow: () => void
  minimize: () => void
  restore: () => void
  setDock: (dock: CompanionDock) => void
  setPosition: (x: number, y: number) => void
  setSize: (w: number, h: number) => void
  setMode: (mode: CompanionMode) => void
  setActiveTab: (tab: CompanionTab) => void
  setBadge: (badge: CompanionBadge) => void
  bumpBadgeCount: (n?: number) => void
  clearBadge: () => void
}

/** Clamp a stored/derived position so the header can never be dragged
 *  fully off-screen (window remains grabbable). */
export function clampToViewport(
  x: number,
  y: number,
  w: number,
  h: number,
): { x: number; y: number } {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800
  const maxX = Math.max(0, vw - Math.min(w, vw))
  const maxY = Math.max(0, vh - 48) // keep at least the header on-screen
  return {
    x: Math.min(Math.max(0, x), maxX),
    y: Math.min(Math.max(0, y), maxY),
  }
}

function defaultFloatPosition(): { x: number; y: number } {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800
  return {
    x: Math.max(16, vw - COMPANION_WINDOW_DEFAULTS.w - 24),
    y: Math.max(16, vh - COMPANION_WINDOW_DEFAULTS.h - 24),
  }
}

export const useCompanionWindowStore = create<CompanionWindowState>()(
  persist(
    (set, get) => ({
      open: false,
      minimized: false,
      // Default = today's behavior: a right-docked panel (the old Dialog).
      dock: 'right' as CompanionDock,
      ...defaultFloatPosition(),
      w: COMPANION_WINDOW_DEFAULTS.w,
      h: COMPANION_WINDOW_DEFAULTS.h,
      mode: 'assistant' as CompanionMode,
      activeTab: 'chat' as CompanionTab,
      badge: null,
      badgeCount: 0,

      openWindow: () => set({ open: true, minimized: false }),
      closeWindow: () => set({ open: false, minimized: false }),
      minimize: () => set({ minimized: true }),
      restore: () => set({ open: true, minimized: false, badge: null, badgeCount: 0 }),
      setDock: (dock) => set({ dock }),
      setPosition: (x, y) => {
        const { w, h } = get()
        set(clampToViewport(x, y, w, h))
      },
      setSize: (w, h) =>
        set({
          w: Math.max(COMPANION_WINDOW_DEFAULTS.minW, Math.round(w)),
          h: Math.max(COMPANION_WINDOW_DEFAULTS.minH, Math.round(h)),
        }),
      setMode: (mode) =>
        set((s) => ({
          mode,
          // Keep the visible tab in sync with the mode unless the user is
          // looking at the task tray.
          activeTab: s.activeTab === 'tasks' ? 'tasks' : mode === 'agent' ? 'agent' : 'chat',
        })),
      setActiveTab: (tab) =>
        set((s) => ({
          activeTab: tab,
          mode: tab === 'agent' ? 'agent' : tab === 'chat' ? 'assistant' : s.mode,
          ...(tab === 'tasks' ? { badge: null, badgeCount: 0 } : {}),
        })),
      setBadge: (badge) => set({ badge }),
      bumpBadgeCount: (n = 1) =>
        set((s) => ({ badgeCount: s.badgeCount + n, badge: s.badge ?? 'task-done' })),
      clearBadge: () => set({ badge: null, badgeCount: 0 }),
    }),
    {
      name: 'sgrc-adminclient-companion-window',
      partialize: (s) => ({
        open: s.open,
        minimized: s.minimized,
        dock: s.dock,
        x: s.x,
        y: s.y,
        w: s.w,
        h: s.h,
        mode: s.mode,
        activeTab: s.activeTab,
      }),
    },
  ),
)

export default useCompanionWindowStore
